import { bindAll } from 'shared/decorators/bindAll';
import { ObservableMap } from 'shared/stores/core/ObservableMap';
import { SimpleMap } from 'shared/stores/core/SimpleMap';
import { DiContainer } from './DiContainer';
import { Dispatcher } from './Dispatcher';

/**
 * This master "Store" is modelled loosely on the Flux pattern. It has states, managed by the stateMap, and actions,
 * managed by the dispatcher. Unlike most Flux libraries it does not distinguish between actions and mutations and
 * there is no restriction against actions mutating state. We still recommend you make the distinction in your actions,
 * using names to imply the usage model. A typical use case is loading data, which updates state, and optionally
 * notifies the system that data's been loaded might look like: "FETCH_SOME_DATA", "UPDATE_STATE", "STATE_UPDATED".
 * All actions in this store are global. An event triggered through the dispatcher can be handled by any store.
 * Triggering an event that is not registered will not cause an error. This simplifies loose coupling between modules.
 *
 * State is managed in the statemap, which is simply a collection of independent states. By design, each store's state
 * is isolated from all others. The intent behind this is to make it hard for stores to modify each-other's state. To
 * make state changes easier to reason about we strongly recommend that each store be the sole curator of its own
 * state; in other words - stores should not mutate another store's state! Call an action instead.
 * A state is attached to a class by defining a "state" member and accessed via the store's "state()" helper method.
 *
 * Getters are functions that should simplify access to complex or computed data in a store. Getters are attached
 * to a class by defining a "getter" member and accessed via the store's "get()" helper method.
 *
 * In addition to things you typically see in flux implementations, there's also a factory and a resource map. The
 * factory is a simple DI container. The resource map contains preconfigured objects that can be used by stores to
 * communicate with a backend API.
 */
@bindAll
export class Store {
  stateMap = new ObservableMap();
  resources = new SimpleMap();
  getters = new SimpleMap();
  dispatcher = new Dispatcher(this);
  factory = new DiContainer(this);
  uScopeApi = null;

  constructor (uScopeGlobal) {
    this.uScopeApi = uScopeGlobal;
  }

  state (name) {
    const [namespace, prop] = name.split('/');
    const module = this.stateMap.get(namespace);
    return prop ? module[prop] : module;
  }

  get (name) {
    const [namespace, prop] = name.split('/');
    const module = this.getters.get(namespace);
    return prop ? module[prop] : module;
  }

  dispatch (action, ...args) {
    return this.dispatcher.dispatch(action, ...args);
  }

  use (extension) {
    return extension.install(this);
  }
}



// WEBPACK FOOTER //
// ./src/shared/stores/core/Store.js